import { createContext, useContext, useEffect, useState, useMemo } from 'react';
import api from '@/lib/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [booted, setBooted] = useState(false);

  useEffect(() => {
    async function boot() {
      try {
        let token;
        try {
          token = localStorage.getItem('metazik_token');
        } catch (e) {
          token = null;
        }

        if (token) {
          try {
            const resp = await api.get('/auth/me');
            setUser(resp.data);
          } catch (err) {
            // Only drop the token when the server actually rejected it
            // (expired/invalid/unknown user). A timeout or network error
            // doesn't mean the token is bad — keep it so the next successful
            // load (or a manual retry) can still sign the user back in,
            // instead of forcing a fresh login every time the backend is slow.
            if (err?.response?.status === 401) {
              try {
                localStorage.removeItem('metazik_token');
              } catch (e) {}
            }
          }
        }
      } catch (err) {
        console.error("Auth boot failed:", err);
      } finally {
        setBooted(true);
      }
    }
    boot();
  }, []);

  const login = async (email, password) => {
    // One retry, but only for a transient failure (cold-start timeout/network
    // error/5xx) — a real rejection (401 bad credentials) is final and must
    // surface immediately instead of making the user wait through a second
    // identical failure.
    let lastErr;
    for (let attempt = 0; attempt < 2; attempt++) {
      try {
        const resp = await api.post('/auth/login', { email, password });
        const { user, token } = resp.data;
        try {
          localStorage.setItem('metazik_token', token);
        } catch (e) {}
        setUser(user);
        return user;
      } catch (e) {
        lastErr = e;
        const status = e?.response?.status;
        if (status && status < 500) throw e;
        if (attempt === 0) await new Promise((r) => setTimeout(r, 1500));
      }
    }
    throw lastErr;
  };

  const register = async (payload) => {
    const resp = await api.post('/auth/register', payload);
    const { user, token } = resp.data;
    try {
      localStorage.setItem('metazik_token', token);
    } catch (e) {}
    setUser(user);
    return user;
  };

  const logout = () => {
    try {
      localStorage.removeItem('metazik_token');
    } catch (e) {}
    setUser(null);
  };

  // Re-fetch the current user from the server — used after editing the
  // profile or uploading a new avatar/cover so the UI reflects the change
  // without a full page reload.
  const refresh = async () => {
    try {
      const resp = await api.get('/auth/me');
      setUser(resp.data);
    } catch (e) {}
  };

  // The account accepts the current Terms of Use (TermsGate, shown after an update).
  const acceptTerms = async () => {
    const resp = await api.post('/auth/accept-terms');
    setUser(resp.data);
  };

  const value = useMemo(() => ({
    user,
    booted,
    login,
    register,
    logout,
    refresh,
    acceptTerms,
    isAdmin: user?.role === 'admin' || user?.role === 'staff',
    isArtist: user?.role === 'artist'
  }), [user, booted]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
